/*! Datastar Inspector v1.1.2 */

// Color Palette: https://lospec.com/palette-list/apollo

declare const ALIAS: string | null

import datastarTemplate from './template.html'

class DatastarInspector extends HTMLElement {
  static get observedAttributes() {
    return ['max-events-visible']
  }

  expanded = false
  open = false
  tab = 'currentSignals'
  maxEventsVisible = 20
  signalPatchEventCount = 0
  sseEventCount = 0
  signalPatchEvents = undefined as HTMLUListElement | undefined
  sseEvents = undefined as HTMLUListElement | undefined
  currentSignalsIncludeFilter = ''
  currentSignalsExcludeFilter = ''
  currentSignalsUseExactMatch = false
  currentSignalsTableView = false
  currentSignals: Record<string, any> = {}
  persistedDataIncludeFilter = ''
  persistedDataExcludeFilter = ''
  persistedDataUseExactMatch = false
  persistedDataTableView = false
  persistedDataUseSessionStorage = false
  persistedData: Record<string, any> = {}
  persistedDataCount = 0
  highlightedElements: Element[] = []
  highlightStyleElement: HTMLStyleElement | null = null

  constructor() {
    super()
    this.attachShadow({ mode: 'open' })

    ;(this as any).__dsSerialize = (signals: any) => {
      const seen = new WeakSet()
      const MAX_DEPTH = 4
      const MAX_KEYS = 200
      const prune = (val: any, depth: number): any => {
        if (val === null || typeof val !== 'object') return val
        if (typeof val === 'function') return '__fn__'
        if (typeof Element !== 'undefined' && val instanceof Element)
          return '__element__'
        if (seen.has(val)) return '__cycle__'
        if (depth >= MAX_DEPTH) return '__truncated__'
        seen.add(val)
        if (Array.isArray(val)) {
          return val.slice(0, MAX_KEYS).map((v) => prune(v, depth + 1))
        }
        const out: Record<string, any> = {}
        let count = 0
        for (const [k, v] of Object.entries(val)) {
          out[k] = prune(v, depth + 1)
          count += 1
          if (count >= MAX_KEYS) {
            out.__truncated__ = true
            break
          }
        }
        return out
      }
      try {
        return JSON.stringify(prune(signals, 0))
      } catch {
        return '{}'
      }
    }

    const datastarInspectorState = sessionStorage.getItem(
      'datastar-inspector-state',
    )
    if (datastarInspectorState) {
      const state = JSON.parse(datastarInspectorState)
      this.expanded = state.expanded ?? this.expanded
      this.open = state.open ?? this.open
      this.tab = state.tab ?? this.tab
      this.currentSignalsIncludeFilter =
        state.currentSignalsIncludeFilter ?? this.currentSignalsIncludeFilter
      this.currentSignalsExcludeFilter =
        state.currentSignalsExcludeFilter ?? this.currentSignalsExcludeFilter
      this.currentSignalsUseExactMatch =
        state.currentSignalsUseExactMatch ?? this.currentSignalsUseExactMatch
      this.currentSignalsTableView =
        state.currentSignalsTableView ?? this.currentSignalsTableView
      this.persistedDataIncludeFilter =
        state.persistedDataIncludeFilter ?? this.persistedDataIncludeFilter
      this.persistedDataExcludeFilter =
        state.persistedDataExcludeFilter ?? this.persistedDataExcludeFilter
      this.persistedDataUseExactMatch =
        state.persistedDataUseExactMatch ?? this.persistedDataUseExactMatch
      this.persistedDataTableView =
        state.persistedDataTableView ?? this.persistedDataTableView
      this.persistedDataUseSessionStorage =
        state.persistedDataUseSessionStorage ??
        this.persistedDataUseSessionStorage
    }
  }

  connectedCallback() {
    this.render()
    this.injectHighlightStyles()

    document.addEventListener('datastar-fetch', this.handleSseEvent.bind(this))
    document.addEventListener(
      'datastar-signal-patch',
      this.handleSignalPatchEvent.bind(this),
    )

    // Listen for storage changes to update persist data count
    window.addEventListener('storage', this.handleStorageChange.bind(this))

    const maxEventsVisibleAttrVal = this.getAttribute('max-events-visible')
    if (maxEventsVisibleAttrVal) {
      const value = Number.parseInt(maxEventsVisibleAttrVal, 10)
      if (!Number.isNaN(value) && value > 0) {
        this.maxEventsVisible = value
      }
    }

    // Initial count update
    this.updatePersistedDataCount()
  }

  disconnectedCallback() {
    this.removeEventListener('datastar-fetch', this.handleSseEvent)
    this.removeEventListener(
      'datastar-signal-patch',
      this.handleSignalPatchEvent,
    )
    window.removeEventListener('storage', this.handleStorageChange)
    this.removeHighlightStyles()
  }

  applyTheme() {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches

    if (this.shadowRoot) {
      this.shadowRoot.host.classList.toggle('dark-theme', isDark)
      this.shadowRoot.host.setAttribute('data-theme', isDark ? 'dark' : 'light')
    }

    // Listen for theme changes
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', (e) => {
        if (this.shadowRoot) {
          this.shadowRoot.host.classList.toggle('dark-theme', e.matches)
          this.shadowRoot.host.setAttribute(
            'data-theme',
            e.matches ? 'dark' : 'light',
          )
        }
      })
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ) {
    if (
      name === 'max-events-visible' &&
      oldValue !== newValue &&
      newValue !== null
    ) {
      this.maxEventsVisible = Number.parseInt(newValue, 10)
      this.garbageCollectEvents(
        this.shadowRoot?.querySelector('#sseEventContent ul'),
      )
      this.garbageCollectEvents(
        this.shadowRoot?.querySelector('#signalPatchEventContent ul'),
      )
    }
  }

  render() {
    if (!this.shadowRoot) {
      return
    }

    this.shadowRoot.innerHTML = datastarTemplate

    // Apply theme after rendering HTML
    this.applyTheme()

    this.renderExpand()
    this.renderOpen()
    this.renderTab()

    for (const button of this.shadowRoot.querySelectorAll(
      '#collapsed, h1 button',
    )) {
      button.addEventListener('click', () => this.toggleExpand())
    }
    for (const button of this.shadowRoot.querySelectorAll(
      '#collapsed, header',
    )) {
      button.addEventListener('click', () => this.toggleOpen())
    }
    for (const element of this.shadowRoot.querySelectorAll(
      'header h1 button, header nav',
    )) {
      element.addEventListener('click', (event) => event.stopPropagation())
    }
    for (const button of this.shadowRoot.querySelectorAll(
      'header nav button',
    )) {
      button.addEventListener('click', () =>
        this.selectTab((button as HTMLButtonElement).dataset.type || ''),
      )
    }
    for (const button of this.shadowRoot.querySelectorAll(
      'footer button[data-type]',
    )) {
      button.addEventListener('click', () =>
        this.clearEvents((button as HTMLButtonElement).dataset.type || ''),
      )
    }

    const signalPatchMaxEventsInput = this.shadowRoot.querySelector(
      '#signalPatchMaxEvents',
    ) as HTMLInputElement
    const sseMaxEventsInput = this.shadowRoot.querySelector(
      '#sseMaxEvents',
    ) as HTMLInputElement

    if (signalPatchMaxEventsInput) {
      signalPatchMaxEventsInput.value = this.maxEventsVisible.toString()
      signalPatchMaxEventsInput.addEventListener('input', () => {
        const value = Number.parseInt(signalPatchMaxEventsInput.value, 10)
        if (!Number.isNaN(value) && value > 0) {
          this.maxEventsVisible = value
          if (sseMaxEventsInput) {
            sseMaxEventsInput.value = value.toString()
          }
          this.garbageCollectEvents(
            this.shadowRoot?.querySelector('#signalPatchEventContent ul'),
          )
          this.garbageCollectEvents(
            this.shadowRoot?.querySelector('#sseEventContent ul'),
          )
        }
      })
    }

    if (sseMaxEventsInput) {
      sseMaxEventsInput.value = this.maxEventsVisible.toString()
      sseMaxEventsInput.addEventListener('input', () => {
        const value = Number.parseInt(sseMaxEventsInput.value, 10)
        if (!Number.isNaN(value) && value > 0) {
          this.maxEventsVisible = value
          if (signalPatchMaxEventsInput) {
            signalPatchMaxEventsInput.value = value.toString()
          }
          this.garbageCollectEvents(
            this.shadowRoot?.querySelector('#signalPatchEventContent ul'),
          )
          this.garbageCollectEvents(
            this.shadowRoot?.querySelector('#sseEventContent ul'),
          )
        }
      })
    }

    this.setupFilterHandlers()
    this.setupPersistedDataHandlers()

    // Update persisted data display if it's the active tab on initial load
    if (this.tab === 'persistedData') {
      this.updatePersistedDataDisplay()
    }
  }

  setupFilterHandlers() {
    if (!this.shadowRoot) return

    // Current Signals filters
    const currentSignalsInclude = this.shadowRoot.querySelector(
      '#currentSignalsIncludeFilter',
    ) as HTMLInputElement
    const currentSignalsExclude = this.shadowRoot.querySelector(
      '#currentSignalsExcludeFilter',
    ) as HTMLInputElement
    const currentSignalsExactMatch = this.shadowRoot.querySelector(
      '#currentSignalsExactMatchCheckbox',
    ) as HTMLInputElement
    const resetFilterBtn = this.shadowRoot.querySelector(
      '#resetFilters',
    ) as HTMLButtonElement
    const copyFilterBtn = this.shadowRoot.querySelector(
      '#copyFilterObject',
    ) as HTMLButtonElement
    const tableViewCheckbox = this.shadowRoot.querySelector(
      '#currentSignalsTableViewCheckbox',
    ) as HTMLInputElement

    if (currentSignalsInclude) {
      currentSignalsInclude.value =
        this.currentSignalsIncludeFilter ||
        (this.currentSignalsUseExactMatch ? '' : '.*')
      currentSignalsInclude.addEventListener('input', () => {
        this.currentSignalsIncludeFilter = currentSignalsInclude.value
        this.saveState()
        this.updateCurrentSignalsDisplay()
        this.updateRegexDisplay()
      })
    }

    if (currentSignalsExclude) {
      currentSignalsExclude.value =
        this.currentSignalsExcludeFilter ||
        (this.currentSignalsUseExactMatch ? '' : '(^|\\.)_')
      currentSignalsExclude.addEventListener('input', () => {
        this.currentSignalsExcludeFilter = currentSignalsExclude.value
        this.saveState()
        this.updateCurrentSignalsDisplay()
        this.updateRegexDisplay()
      })
    }

    if (currentSignalsExactMatch) {
      currentSignalsExactMatch.checked = this.currentSignalsUseExactMatch
      currentSignalsExactMatch.addEventListener('change', () => {
        this.currentSignalsUseExactMatch = currentSignalsExactMatch.checked
        this.saveState()
        this.updateCurrentSignalsDisplay()
        this.updateRegexDisplay()
      })
    }

    if (resetFilterBtn) {
      resetFilterBtn.addEventListener('click', () => {
        this.currentSignalsIncludeFilter = '.*'
        this.currentSignalsExcludeFilter = '(^|\\.)_'
        this.currentSignalsUseExactMatch = false

        currentSignalsInclude.value = this.currentSignalsIncludeFilter
        currentSignalsExclude.value = this.currentSignalsExcludeFilter
        currentSignalsExactMatch.checked = this.currentSignalsUseExactMatch

        this.saveState()
        this.updateCurrentSignalsDisplay()
        this.updateRegexDisplay()
      })
    }

    if (copyFilterBtn) {
      copyFilterBtn.addEventListener('click', () => {
        this.copyFilterObject(copyFilterBtn)
      })
    }

    if (tableViewCheckbox) {
      tableViewCheckbox.checked = this.currentSignalsTableView
      tableViewCheckbox.addEventListener('change', () => {
        this.currentSignalsTableView = tableViewCheckbox.checked
        this.saveState()
        this.updateCurrentSignalsDisplay()
      })
    }

    // Initial regex display update
    this.updateRegexDisplay()
  }

  exactMatchAsRegex(pattern: string): RegExp {
    return RegExp(`^${pattern.replace(/\./g, '\\.').replace(/\*/g, '.*')}$`)
  }

  flattenObject(
    obj: Record<string, any>,
    prefix = '',
    depth = 0,
  ): Array<[string, any]> {
    const MAX_DEPTH = 6
    const result: Array<[string, any]> = []

    for (const [key, value] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${key}` : key

      // Ignore empty Rocket instance placeholders like _rocket.foo._10: ""
      if (/^_rocket\.[^.]+\._\d+$/.test(path) && value === '') {
        continue
      }

      // Special handling for computed values: show their resolved result
      if (
        (key === 'computed' || path.endsWith('.computed')) &&
        value &&
        typeof value === 'object'
      ) {
        const entries = Object.entries(value)
        if (entries.length > 0) {
          for (const [compKey, compVal] of entries) {
            const compPath = `${path}.${compKey}`
            let resolved: any = compVal
            if (typeof compVal === 'function') {
              try {
                resolved = compVal()
              } catch {
                resolved = '__error__'
              }
            }
            if (resolved !== null && typeof resolved === 'object') {
              result.push([compPath, '__object__'])
              if (depth < MAX_DEPTH) {
                result.push(
                  ...this.flattenObject(resolved, compPath, depth + 1),
                )
              } else {
                result.push([`${compPath}.__truncated__`, true])
              }
            } else {
              result.push([compPath, resolved])
            }
          }
        }
        continue
      }

      if (value !== null && typeof value === 'object') {
        result.push([path, '__object__'])
        if (depth < MAX_DEPTH) {
          if (Array.isArray(value)) {
            value.forEach((item, index) => {
              if (item !== null && typeof item === 'object') {
                result.push(
                  ...this.flattenObject(item, `${path}.${index}`, depth + 1),
                )
              } else {
                result.push([`${path}.${index}`, item])
              }
            })
          } else {
            result.push(...this.flattenObject(value, path, depth + 1))
          }
        } else {
          result.push([`${path}.__truncated__`, true])
        }
      } else {
        result.push([path, value])
      }
    }

    return result
  }

  updateRegexDisplay() {
    if (!this.shadowRoot) return

    const regexDisplay = this.shadowRoot.querySelector(
      '#currentSignalsRegexDisplay',
    ) as HTMLElement
    const includeSpan = regexDisplay?.querySelector(
      '.regex-include',
    ) as HTMLElement
    const excludeSpan = regexDisplay?.querySelector(
      '.regex-exclude',
    ) as HTMLElement

    if (!regexDisplay || !includeSpan || !excludeSpan) return

    regexDisplay.style.display = ''

    const include = this.currentSignalsIncludeFilter || '.*'
    const exclude = this.currentSignalsExcludeFilter || '(^|\\.)_'

    includeSpan.textContent =
      this.currentSignalsUseExactMatch && this.currentSignalsIncludeFilter
        ? this.exactMatchAsRegex(include).toString()
        : `/${include}/`
    excludeSpan.textContent =
      this.currentSignalsUseExactMatch && this.currentSignalsExcludeFilter
        ? this.exactMatchAsRegex(exclude).toString()
        : `/${exclude}/`
  }

  copyFilterObject(button: HTMLButtonElement) {
    const include = this.currentSignalsIncludeFilter || '.*'
    const exclude = this.currentSignalsExcludeFilter || '(^|\\.)_'

    const filterObj = {
      include: this.currentSignalsUseExactMatch
        ? this.exactMatchAsRegex(include).toString()
        : `/${include}/`,
      exclude: this.currentSignalsUseExactMatch
        ? this.exactMatchAsRegex(exclude).toString()
        : `/${exclude}/`,
    }

    const text = `{ include: ${filterObj.include}, exclude: ${filterObj.exclude} }`
    const spanButton = button.querySelector('span') as HTMLSpanElement
    navigator.clipboard
      .writeText(text)
      .then(() => {
        spanButton.textContent = 'Copied'
        spanButton.style.display = ''
      })
      .catch(() => {
        spanButton.textContent = 'Copy failed'
        spanButton.style.display = ''
      })
      .finally(() => {
        setTimeout(() => {
          spanButton.style.display = 'none'
        }, 2000)
      })
  }

  updateCurrentSignalsDisplay() {
    if (this.currentSignalsTableView) {
      this.updateCurrentSignalsTableDisplay()
    } else {
      this.updateCurrentSignalsTreeDisplay()
    }
  }

  updateCurrentSignalsTableDisplay() {
    const hasFilters =
      this.currentSignalsIncludeFilter || this.currentSignalsExcludeFilter

    // Flatten the object to key-value pairs
    const flatEntries = this.flattenObject(this.currentSignals)

    // Filter and count matches
    let matchedCount = 0
    const filteredEntries = flatEntries.map(([path, value]) => {
      const matches = this.matchesFilter(
        path,
        this.currentSignalsIncludeFilter,
        this.currentSignalsExcludeFilter,
        this.currentSignalsUseExactMatch,
      )
      if (matches) matchedCount++
      return { path, value, matches }
    })

    // Build the table HTML
    const tableRows = filteredEntries
      .map(({ path, value, matches }) => {
        const valueStr = JSON.stringify(value)
        const rowClass = hasFilters ? (matches ? 'matched' : 'filtered') : ''
        return `<tr${rowClass ? ` class="${rowClass}"` : ''}><td>${this.escapeHtml(path)}</td><td>${this.escapeHtml(valueStr)}</td></tr>`
      })
      .join('\n')

    const tableHtml = `
      <table class="signals-table">
        <thead>
          <tr>
            <th>Path</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
    `

    this.setTextContent(
      '#currentSignalsCount',
      hasFilters ? `${matchedCount}/${flatEntries.length}` : flatEntries.length,
    )

    // Replace the entire li content with the table
    const liElement = this.shadowRoot?.querySelector(
      '#currentSignalsContent ul li',
    )
    if (liElement) {
      liElement.innerHTML = `<div style="flex: 1; overflow-x: auto;">${tableHtml}</div>`
      this.addCurrentSignalsButtons(liElement)
      this.addHoverHandlersToSignalPaths(liElement)
    }
  }

  escapeHtml(text: string): string {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  updateCurrentSignalsTreeDisplay() {
    const hasFilters =
      this.currentSignalsIncludeFilter || this.currentSignalsExcludeFilter

    // If filters are active, check all nested paths to determine matches
    const pathMatches: Set<string> = new Set()
    let matchedLeafPaths = 0

    if (hasFilters) {
      const flatEntries = this.flattenObject(this.currentSignals)
      for (const [path] of flatEntries) {
        if (
          this.matchesFilter(
            path,
            this.currentSignalsIncludeFilter,
            this.currentSignalsExcludeFilter,
            this.currentSignalsUseExactMatch,
          )
        ) {
          matchedLeafPaths++
          // Add this path and all its parent paths to the match set
          const parts = path.split('.')
          for (let i = 1; i <= parts.length; i++) {
            pathMatches.add(parts.slice(0, i).join('.'))
          }
        }
      }
    }

    // Build the display recursively with proper highlighting
    const resolveComputedEntries = (val: any): Record<string, any> => {
      const out: Record<string, any> = {}
      if (val && typeof val === 'object') {
        for (const [k, v] of Object.entries(val)) {
          if (typeof v === 'function') {
            try {
              out[k] = v()
            } catch {
              out[k] = '__error__'
            }
          } else {
            out[k] = v
          }
        }
      }
      return out
    }

    const buildTreeDisplay = (
      obj: any,
      prefix = '',
      indent = '  ',
    ): string[] => {
      const lines: string[] = []
      const entries = Object.entries(obj)

      entries.forEach(([key, value], index) => {
        // Resolve computed() entries to their current values
        if (
          (key === 'computed' ||
            (prefix && `${prefix}.${key}`.endsWith('.computed'))) &&
          value &&
          typeof value === 'object'
        ) {
          const resolved = resolveComputedEntries(value)
          if (Object.keys(resolved).length === 0) {
            return
          }
          value = resolved
        }

        const currentPath = prefix ? `${prefix}.${key}` : key
        const isLast = index === entries.length - 1
        const hasMatch = pathMatches.has(currentPath)

        // Determine span attributes based on filter state
        let spanAttrs: string
        if (hasFilters) {
          spanAttrs = hasMatch ? 'class="matched"' : 'class="filtered"'
        } else {
          spanAttrs = `data-path="${currentPath}"`
        }

        if (
          value !== null &&
          typeof value === 'object' &&
          !Array.isArray(value)
        ) {
          // Handle nested objects
          lines.push(`${indent}<span ${spanAttrs}>"${key}": {`)

          const nestedLines = buildTreeDisplay(
            value,
            currentPath,
            `${indent}  `,
          )
          lines.push(...nestedLines)

          lines.push(`${indent}}${isLast ? '</span>' : ',</span>'}`)
        } else {
          // Handle primitive values and arrays
          const valueStr = JSON.stringify(value, null, 2)
          const valueLines = valueStr.split('\n')

          lines.push(
            `${indent}<span ${spanAttrs}>"${key}": ${this.escapeHtml(valueLines[0])}`,
          )
          for (let i = 1; i < valueLines.length; i++) {
            lines.push(`${indent}${this.escapeHtml(valueLines[i])}`)
          }
          lines[lines.length - 1] += isLast ? '</span>' : ',</span>'
        }
      })

      return lines
    }

    // Build the complete display
    const lines: string[] = []

    // Add opening brace - highlight if filters are active and have matches
    const shouldHighlightBraces = hasFilters && matchedLeafPaths > 0
    lines.push(shouldHighlightBraces ? '<span class="matched">{</span>' : '{')

    // Add the tree content
    lines.push(...buildTreeDisplay(this.currentSignals))

    // Add closing brace
    lines.push(shouldHighlightBraces ? '<span class="matched">}</span>' : '}')

    // Update the count
    const totalCount = this.flattenObject(this.currentSignals).length
    this.setTextContent(
      '#currentSignalsCount',
      hasFilters ? `${matchedLeafPaths}/${totalCount}` : totalCount,
    )

    // Replace the entire li content with a pre element containing the tree
    const liElement = this.shadowRoot?.querySelector(
      '#currentSignalsContent ul li',
    )
    if (liElement) {
      liElement.innerHTML = `<div style="flex: 1; overflow-x: auto;"><pre>${lines.join('\n')}</pre></div>`
      this.addCurrentSignalsButtons(liElement)
      this.addHoverHandlersToSignalPaths(liElement)
    }
  }

  handleSignalPatchEvent(event: Event) {
    const detail = (event as CustomEvent).detail
    // Use a deep clone that preserves nested paths; avoid safeSerialize here
    let cleanPatch: Record<string, any> = detail
    try {
      // JSON clone is enough for signal patch payloads (plain data)
      cleanPatch = JSON.parse(JSON.stringify(detail))
    } catch {
      // fall back to the original detail if cloning fails
    }

    const summary = this.safeSerialize(detail)

    // Debug incoming patch structure to validate flattening/merging logic
    // Remove noisy debug logging now that patch handling is fixed

    this.applyPatch(cleanPatch)

    this.signalPatchEventCount++
    this.setTextContent('#signalPatchEventCount', this.signalPatchEventCount)

    this.appendEvent(
      this.signalPatchEventCount,
      '#signalPatchEventContent',
      summary,
      this.safeSerialize(detail, true),
      detail,
      true,
    )
  }

  private safeSerialize(val: any, pretty = false): string {
    const seen = new WeakSet()
    const MAX_DEPTH = 4
    const MAX_KEYS = 200
    const prune = (v: any, depth: number): any => {
      if (v === null || typeof v !== 'object') return v
      if (typeof v === 'function') return '__fn__'
      if (typeof Element !== 'undefined' && v instanceof Element)
        return '__element__'
      if (seen.has(v)) return '__cycle__'
      if (depth >= MAX_DEPTH) return '__truncated__'
      seen.add(v)
      if (Array.isArray(v)) {
        return v.slice(0, MAX_KEYS).map((item) => prune(item, depth + 1))
      }
      const out: Record<string, any> = {}
      let count = 0
      for (const [k, val] of Object.entries(v)) {
        out[k] = prune(val, depth + 1)
        count += 1
        if (count >= MAX_KEYS) {
          out.__truncated__ = true
          break
        }
      }
      return out
    }
    try {
      return JSON.stringify(prune(val, 0), null, pretty ? 2 : undefined)
    } catch {
      return '{}'
    }
  }

  private applyPatch(patch: Record<string, any>) {
    const flattenPatch = (
      obj: Record<string, any>,
      prefix = '',
      acc: Array<[string, any]> = [],
    ): Array<[string, any]> => {
      for (const [key, val] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${key}` : key
        if (
          val &&
          typeof val === 'object' &&
          !Array.isArray(val) &&
          Object.keys(val).length > 0
        ) {
          flattenPatch(val, path, acc)
        } else {
          acc.push([path, val])
        }
      }
      return acc
    }

    const applyEntry = (path: string, val: any) => {
      const parts = path.split('.')
      let target: any = this.currentSignals
      for (let i = 0; i < parts.length - 1; i += 1) {
        const key = parts[i]
        if (
          target[key] === undefined ||
          target[key] === null ||
          typeof target[key] !== 'object'
        ) {
          target[key] = {}
        }
        target = target[key]
      }
      const leaf = parts[parts.length - 1]
      if (val === null) {
        delete target[leaf]
      } else {
        target[leaf] = val
      }
    }

    for (const [path, val] of flattenPatch(patch)) {
      // Drop empty-string rocket instance placeholders
      if (val === '' && /^_rocket\.[^.]+\._\d+$/.test(path)) {
        applyEntry(path, null)
        continue
      }
      applyEntry(path, val)
    }

    this.updateCurrentSignalsDisplay()
  }

  handleSseEvent(event: Event) {
    const detail = (event as CustomEvent).detail
    if (!detail.type.startsWith('datastar-')) {
      return
    }

    this.sseEventCount++
    this.setTextContent('#sseEventCount', this.sseEventCount)

    let content = ''
    const argsRaw = detail.argsRaw as Record<string, any>
    for (const [key, value] of Object.entries(argsRaw)) {
      content += `data: ${key} ${value}\n`
    }
    // Remove trailing newline
    content = content.trimEnd()
    this.appendEvent(
      this.sseEventCount,
      '#sseEventContent',
      `event: ${detail.type}`,
      content,
      detail,
      false,
    )
  }

  toggleExpand() {
    this.expanded = !this.expanded
    this.renderExpand()
    this.saveState()
  }

  renderExpand() {
    this.setVisibility('#collapsed', !this.expanded)
    this.setVisibility('header', this.expanded)
    if (!this.expanded) {
      this.open = true
      this.toggleOpen()
    }
  }

  toggleOpen() {
    this.open = !this.open
    this.renderOpen()
    this.saveState()
  }

  renderOpen() {
    this.setVisibility('main', this.open)
  }

  selectTab(tab: string) {
    this.open = true
    this.tab = tab
    this.renderTab()
    this.saveState()

    // Update persisted data display when switching to persistedData tab
    if (tab === 'persistedData') {
      this.updatePersistedDataDisplay()
    }
  }

  renderTab() {
    this.setAttributeValue('header button', 'aria-selected', 'false')
    this.setAttributeValue(
      `header button[data-type=${this.tab}]`,
      'aria-selected',
      'true',
    )
    this.setVisibility('main', this.open)
    this.setVisibility('main section', false)
    this.setVisibility(`#${this.tab}Content`, true)
    this.saveState()
  }

  saveState() {
    const state = {
      expanded: this.expanded,
      open: this.open,
      tab: this.tab,
      currentSignalsIncludeFilter: this.currentSignalsIncludeFilter,
      currentSignalsExcludeFilter: this.currentSignalsExcludeFilter,
      currentSignalsUseExactMatch: this.currentSignalsUseExactMatch,
      currentSignalsTableView: this.currentSignalsTableView,
      persistedDataIncludeFilter: this.persistedDataIncludeFilter,
      persistedDataExcludeFilter: this.persistedDataExcludeFilter,
      persistedDataUseExactMatch: this.persistedDataUseExactMatch,
      persistedDataTableView: this.persistedDataTableView,
      persistedDataUseSessionStorage: this.persistedDataUseSessionStorage,
    }
    sessionStorage.setItem('datastar-inspector-state', JSON.stringify(state))
  }

  clearEvents(tab: string) {
    if (tab === 'sse') {
      this.sseEventCount = 0
    } else {
      this.signalPatchEventCount = 0
    }

    this.setTextContent(`#${tab}Count`, 0)
    this.setInnerHtml(`#${tab}Content ul`, '')
    this.setVisibility(`#${tab}Content footer button[data-type]`, false)
    this.setVisibility(`#${tab}Content p`, true)
  }

  setTextContent(selector: string, value: string | number) {
    const element = this.shadowRoot?.querySelector(selector)
    if (element) {
      element.textContent = `${value}`
    }
  }

  setInnerHtml(selector: string, value: string) {
    const element = this.shadowRoot?.querySelector(selector)
    if (element) {
      element.innerHTML = value
    }
  }

  setAttributeValue(selector: string, key: string, value: string) {
    for (const element of this.shadowRoot?.querySelectorAll(selector) || []) {
      element.setAttribute(key, value)
    }
  }

  setVisibility(selector: string, visible: boolean) {
    this.setAttributeValue(selector, 'style', visible ? '' : 'display: none;')
  }

  appendEvent(
    count: number,
    selector: string,
    summary: string,
    content: string,
    raw: object,
    blurrable: boolean,
  ) {
    const element = this.shadowRoot?.querySelector(`${selector} ul`)
    if (!element) {
      return
    }

    const pre = document.createElement('pre')
    pre.textContent = content
    const div = document.createElement('div')
    div.innerHTML = `
      <span>${count}.</span>
      <details>
        <summary class="${blurrable ? 'blurrable' : ''}">${summary}</summary>
        <pre>${pre.innerHTML}</pre>
      </details>
    `

    // Clone copy button from template
    const copyTemplate = this.shadowRoot?.querySelector(
      '#copy-button-template',
    ) as HTMLTemplateElement
    const copyButton = copyTemplate.content.cloneNode(true) as DocumentFragment
    const copyBtn = copyButton.querySelector('button') as HTMLButtonElement
    const copyBtnSpan = copyBtn.querySelector('span') as HTMLSpanElement

    copyBtn.onclick = () => {
      // Include both the event type (summary) and data (content) for complete SSE format
      const fullContent = summary.startsWith('event:')
        ? `${summary}\n${content}`
        : content
      navigator.clipboard
        .writeText(fullContent)
        .then(() => {
          copyBtnSpan.textContent = 'Copied'
          copyBtnSpan.style.display = ''
        })
        .catch(() => {
          copyBtnSpan.textContent = 'Copy Failed'
          copyBtnSpan.style.display = ''
        })
        .finally(() => {
          setTimeout(() => {
            copyBtnSpan.style.display = 'none'
          }, 2000)
        })
    }

    // Clone log button from template
    const logTemplate = this.shadowRoot?.querySelector(
      '#log-button-template',
    ) as HTMLTemplateElement
    const logButton = logTemplate.content.cloneNode(true) as DocumentFragment
    const logBtn = logButton.querySelector('button') as HTMLButtonElement
    const logBtnSpan = logBtn.querySelector('span') as HTMLSpanElement

    logBtn.onclick = () => {
      console.log(raw)
      logBtnSpan.textContent = 'Logged'
      logBtnSpan.style.display = ''
      setTimeout(() => {
        logBtnSpan.style.display = 'none'
      }, 2000)
    }

    const li = document.createElement('li')
    li.appendChild(div)
    li.appendChild(copyBtn)
    li.appendChild(logBtn)
    element.prepend(li)

    this.garbageCollectEvents(element)

    this.setVisibility(`${selector} footer button[data-type]`, true)
    this.setVisibility(`${selector} p`, false)
  }

  createButton(text: string, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button')
    button.textContent = text
    button.onclick = onClick

    return button
  }

  garbageCollectEvents(element: Element | null | undefined) {
    if (!element) {
      return
    }

    while (element.children.length > this.maxEventsVisible) {
      element.removeChild(element.lastChild as ChildNode)
    }
  }

  addCurrentSignalsButtons(liElement: Element) {
    // Clone copy button from template
    const copyTemplate = this.shadowRoot?.querySelector(
      '#copy-button-template',
    ) as HTMLTemplateElement
    const copyButton = copyTemplate.content.cloneNode(true) as DocumentFragment
    const copyBtn = copyButton.querySelector('button') as HTMLButtonElement
    const copyBtnSpan = copyBtn.querySelector('span') as HTMLSpanElement

    copyBtn.onclick = () => {
      const jsonStr = JSON.stringify(this.currentSignals, null, 2)
      navigator.clipboard
        .writeText(jsonStr)
        .then(() => {
          copyBtnSpan.textContent = 'Copied'
          copyBtnSpan.style.display = ''
        })
        .catch(() => {
          copyBtnSpan.textContent = 'Copy Failed'
          copyBtnSpan.style.display = ''
        })
        .finally(() => {
          setTimeout(() => {
            copyBtnSpan.style.display = 'none'
          }, 2000)
        })
    }

    // Clone log button from template
    const logTemplate = this.shadowRoot?.querySelector(
      '#log-button-template',
    ) as HTMLTemplateElement
    const logButton = logTemplate.content.cloneNode(true) as DocumentFragment
    const logBtn = logButton.querySelector('button') as HTMLButtonElement
    const logBtnSpan = logBtn.querySelector('span') as HTMLSpanElement

    logBtn.onclick = () => {
      console.log(this.currentSignals)
      logBtnSpan.textContent = 'Logged'
      logBtnSpan.style.display = ''
      setTimeout(() => {
        logBtnSpan.style.display = 'none'
      }, 2000)
    }

    liElement.appendChild(copyBtn)
    liElement.appendChild(logBtn)

    // Add hover handlers for signal path highlighting
    this.addHoverHandlersToSignalPaths(liElement)
  }

  matchesFilter(
    text: string,
    includeFilter: string,
    excludeFilter: string,
    useWildcard: boolean,
  ): boolean {
    // Use default regex patterns if filters are empty
    const include = includeFilter || '.*'
    const exclude = excludeFilter || '(^|\\.)_'

    if (useWildcard) {
      const includeRe = this.exactMatchAsRegex(include)
      const excludeRe = this.exactMatchAsRegex(exclude)
      return includeRe.test(text) && !excludeRe.test(text)
    }

    // Treat as regex patterns
    try {
      const includeRe = new RegExp(include)
      const excludeRe = new RegExp(exclude)
      return includeRe.test(text) && !excludeRe.test(text)
    } catch {
      // If regex is invalid, fall back to substring matching
      return text.includes(include) && !text.includes(exclude)
    }
  }

  // Inject highlight styles into main document
  injectHighlightStyles() {
    if (this.highlightStyleElement) return // Already injected

    this.highlightStyleElement = document.createElement('style')
    this.highlightStyleElement.textContent = `
      @keyframes datastar-highlight-pulse {
        0% {
          box-shadow: 
            0 0 0 2px rgba(165, 153, 255, 0.8),
            0 0 0 6px rgba(165, 153, 255, 0.4),
            0 0 16px 4px rgba(165, 153, 255, 0.6);
          background-color: rgba(165, 153, 255, 0.2);
        }
        50% {
          box-shadow: 
            0 0 0 4px rgba(165, 153, 255, 1),
            0 0 0 12px rgba(165, 153, 255, 0.6),
            0 0 32px 8px rgba(165, 153, 255, 0.8);
          background-color: rgba(165, 153, 255, 0.4);
        }
        100% {
          box-shadow: 
            0 0 0 2px rgba(165, 153, 255, 0.8),
            0 0 0 6px rgba(165, 153, 255, 0.4),
            0 0 16px 4px rgba(165, 153, 255, 0.6);
          background-color: rgba(165, 153, 255, 0.2);
        }
      }
      
      .datastar-signal-highlight {
        animation: datastar-highlight-pulse 0.8s ease-in-out infinite !important;
        position: relative !important;
        z-index: 9998 !important;
      }
    `
    document.head.appendChild(this.highlightStyleElement)
  }

  // Remove highlight styles from main document
  removeHighlightStyles() {
    if (this.highlightStyleElement) {
      document.head.removeChild(this.highlightStyleElement)
      this.highlightStyleElement = null
    }
  }

  // Signal highlighting functionality
  findElementsUsingSignal(signalPath: string): Element[] {
    const elements: Element[] = []
    const signalPatterns = this.generateSignalPatterns(signalPath)

    // Search all elements with any data-* attributes (more comprehensive)
    const allElements = document.querySelectorAll('*')

    for (const element of allElements) {
      const attributes = element.attributes
      for (const attr of attributes) {
        if (
          attr.name.startsWith('data-') &&
          this.attributeUsesSignal(attr.value, signalPatterns)
        ) {
          elements.push(element)
          break // Found match, no need to check other attributes on this element
        }
      }
    }

    return elements
  }

  generateSignalPatterns(signalPath: string): string[] {
    const patterns = []

    // For signal path "foo.bar", generate patterns:
    // $foo.bar, $foo
    const parts = signalPath.split('.')
    for (let i = parts.length; i > 0; i--) {
      const path = parts.slice(0, i).join('.')
      patterns.push(`$${path}`)
    }

    return patterns
  }

  attributeUsesSignal(
    attributeValue: string,
    signalPatterns: string[],
  ): boolean {
    for (const pattern of signalPatterns) {
      // Check for exact signal references or signal followed by operators
      const regex = new RegExp(
        `\\${pattern.replace('.', '\\.')}(?![a-zA-Z0-9_])`,
        'g',
      )
      if (regex.test(attributeValue)) {
        return true
      }
    }
    return false
  }

  highlightElements(elements: Element[]) {
    // Clear previous highlights
    this.clearHighlights()

    // Add highlight class to matching elements
    for (const element of elements) {
      element.classList.add('datastar-signal-highlight')
      this.highlightedElements.push(element)
    }
  }

  clearHighlights() {
    for (const element of this.highlightedElements) {
      element.classList.remove('datastar-signal-highlight')
    }
    this.highlightedElements = []
  }

  addHoverHandlersToSignalPaths(
    container: Element,
    dataType: 'current' | 'persisted' = 'current',
  ) {
    // Find all signal path text nodes in the tree/table view
    const signalPaths = this.extractSignalPaths(container, dataType)

    for (const pathInfo of signalPaths) {
      pathInfo.element.classList.add('signal-path-hoverable')

      pathInfo.element.addEventListener('mouseenter', () => {
        const elements = this.findElementsUsingSignal(pathInfo.path)
        this.highlightElements(elements)
      })

      pathInfo.element.addEventListener('mouseleave', () => {
        this.clearHighlights()
      })
    }
  }

  extractSignalPaths(
    container: Element,
    dataType: 'current' | 'persisted',
  ): Array<{ element: Element; path: string }> {
    const paths: Array<{ element: Element; path: string }> = []
    const isTableView =
      dataType === 'current'
        ? this.currentSignalsTableView
        : this.persistedDataTableView
    const dataSource =
      dataType === 'current' ? this.currentSignals : this.persistedData

    if (isTableView) {
      // Table view - look for all path cells (both matched and filtered)
      const pathCells = container.querySelectorAll('td:first-child')
      for (const cell of pathCells) {
        const path = cell.textContent?.trim()
        if (path) {
          // Add hover capability to all paths, regardless of filter state
          paths.push({ element: cell, path })
        }
      }
    } else {
      // Tree view - extract paths from all visible spans, not just matched ones
      const preElements =
        dataType === 'persisted'
          ? Array.from(container.querySelectorAll('pre'))
          : [container.querySelector('pre')].filter(
              (el): el is HTMLPreElement => el !== null,
            )

      for (const preElement of preElements) {
        // Get all spans (both matched and filtered)
        const spans = preElement.querySelectorAll('span')
        const processedPaths = new Set<string>()

        // For each span, extract the path from its content
        for (const span of spans) {
          // First check if span has data-path attribute (used when no filters)
          const dataPath = span.getAttribute('data-path')
          if (dataPath && !processedPaths.has(dataPath)) {
            processedPaths.add(dataPath)
            paths.push({ element: span, path: dataPath })
            continue
          }

          const text = span.textContent || ''

          // Match property names in quotes
          const propertyMatches = text.match(/"([^"]+)":\s*/)
          if (propertyMatches) {
            const property = propertyMatches[1]

            // Find the full path for this property by matching against data source
            if (dataType === 'current') {
              const flatEntries = this.flattenObject(dataSource)
              for (const [fullPath] of flatEntries) {
                if (fullPath.endsWith(property) || fullPath === property) {
                  // Avoid duplicates
                  if (!processedPaths.has(fullPath)) {
                    processedPaths.add(fullPath)
                    paths.push({ element: span, path: fullPath })
                  }
                  break
                }
              }
            } else {
              // For persisted data, iterate through all data keys
              for (const [, value] of Object.entries(dataSource)) {
                const flatEntries = this.flattenObject(value)
                for (const [fullPath] of flatEntries) {
                  if (fullPath.endsWith(property) || fullPath === property) {
                    if (!processedPaths.has(fullPath)) {
                      processedPaths.add(fullPath)
                      paths.push({ element: span, path: fullPath })
                    }
                    break
                  }
                }
              }
            }
          }
        }

        // Also process text nodes that aren't in spans
        const walker = document.createTreeWalker(
          preElement,
          NodeFilter.SHOW_TEXT,
          {
            acceptNode: (node) => {
              // Skip text nodes that are inside spans we already processed
              return node.parentElement?.tagName === 'SPAN'
                ? NodeFilter.FILTER_SKIP
                : NodeFilter.FILTER_ACCEPT
            },
          },
        )

        let textNode: Node | null
        while ((textNode = walker.nextNode())) {
          const text = textNode.textContent || ''
          const propertyMatches = text.match(/"([^"]+)":\s*/)

          if (propertyMatches) {
            const property = propertyMatches[1]
            const parentElement = textNode.parentElement

            if (parentElement) {
              const processedPaths = new Set<string>()

              if (dataType === 'current') {
                const flatEntries = this.flattenObject(dataSource)
                for (const [fullPath] of flatEntries) {
                  if (fullPath.endsWith(property) || fullPath === property) {
                    if (!processedPaths.has(fullPath)) {
                      processedPaths.add(fullPath)
                      paths.push({ element: parentElement, path: fullPath })
                    }
                    break
                  }
                }
              } else {
                // For persisted data, iterate through all data keys
                for (const [, value] of Object.entries(dataSource)) {
                  const flatEntries = this.flattenObject(value)
                  for (const [fullPath] of flatEntries) {
                    if (fullPath.endsWith(property) || fullPath === property) {
                      if (!processedPaths.has(fullPath)) {
                        processedPaths.add(fullPath)
                        paths.push({ element: parentElement, path: fullPath })
                      }
                      break
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    return paths
  }

  handleStorageChange() {
    // Update persist data count when storage changes
    this.updatePersistedDataCount()

    // If we're currently on the persistedData tab, refresh the display
    if (this.tab === 'persistedData') {
      this.updatePersistedDataDisplay()
    }
  }

  updatePersistedDataCount() {
    const storage = this.persistedDataUseSessionStorage
      ? sessionStorage
      : localStorage

    // Count only datastar persist keys that exist in storage
    const persistKeys = this.getDatastarPersistKeys()
    const count = persistKeys.filter(
      (key) => storage.getItem(key) !== null,
    ).length
    this.setTextContent('#persistedDataCount', count)
  }

  getDatastarPersistKeys(): string[] {
    const keys = new Set<string>()

    // Find all elements and check their attributes
    const allElements = document.querySelectorAll('*')

    for (const element of allElements) {
      for (const attr of element.attributes) {
        // Check if attribute starts with 'data-persist'
        if (attr.name.startsWith('data-persist')) {
          const attrName = attr.name

          if (attrName === 'data-persist') {
            // Plain data-persist uses default 'datastar' key
            keys.add('datastar')
          } else if (attrName.startsWith('data-persist-')) {
            // Extract key from data-persist:keyname format
            const suffix = attrName.substring('data-persist-'.length)
            // Split by __ to separate key from modifiers
            const [keyPart] = suffix.split('__')

            if (keyPart) {
              keys.add(keyPart)
            }
          }
        }
      }
    }

    return Array.from(keys)
  }

  updatePersistedDataDisplay() {
    const storage = this.persistedDataUseSessionStorage
      ? sessionStorage
      : localStorage

    // Find all data-persist elements on the page to get the keys being used
    const persistKeys = this.getDatastarPersistKeys()

    const data: Record<string, any> = {}
    const keys: string[] = []

    for (const key of persistKeys) {
      const value = storage.getItem(key)
      if (value !== null) {
        keys.push(key)
        try {
          data[key] = JSON.parse(value)
        } catch {
          data[key] = value // Keep as string if not JSON
        }
      }
    }

    this.persistedData = data
    this.persistedDataCount = keys.length
    this.setTextContent('#persistedDataCount', this.persistedDataCount)

    // Show/hide no data message
    const noDataMsg = this.shadowRoot?.querySelector(
      '#persistedDataContent p',
    ) as HTMLElement
    if (noDataMsg) {
      noDataMsg.style.display = keys.length === 0 ? '' : 'none'
    }

    if (keys.length === 0) {
      const ulElement = this.shadowRoot?.querySelector(
        '#persistedDataContent ul',
      )
      if (ulElement) {
        ulElement.innerHTML = ''
      }
      return
    }

    // Use the same display logic as current signals for each storage key
    if (this.persistedDataTableView) {
      this.updatePersistedDataTableDisplay()
    } else {
      this.updatePersistedDataTreeDisplay()
    }
  }

  updatePersistedDataTableDisplay() {
    const storage = this.persistedDataUseSessionStorage
      ? sessionStorage
      : localStorage
    const persistKeys = this.getDatastarPersistKeys()
    const data: Record<string, any> = {}

    for (const key of persistKeys) {
      const value = storage.getItem(key)
      if (value !== null) {
        try {
          data[key] = JSON.parse(value)
        } catch {
          data[key] = value
        }
      }
    }

    const hasFilters =
      this.persistedDataIncludeFilter || this.persistedDataExcludeFilter

    let totalMatchedCount = 0
    let totalCount = 0

    // Build HTML sections for each key with its own captioned table
    const sections = Object.entries(data)
      .map(([key, value]) => {
        // Flatten the persisted data for this key
        const flatEntries = this.flattenObject(value)
        totalCount += flatEntries.length

        // Filter and count matches for this key
        const filteredEntries = flatEntries.map(([path, val]) => {
          const matches = this.matchesFilter(
            path,
            this.persistedDataIncludeFilter,
            this.persistedDataExcludeFilter,
            this.persistedDataUseExactMatch,
          )
          if (matches) {
            totalMatchedCount++
          }
          return { path, value: val, matches }
        })

        // Build the table HTML for this key
        const tableRows = filteredEntries
          .map(({ path, value, matches }) => {
            const valueStr = JSON.stringify(value)
            const rowClass = hasFilters
              ? matches
                ? 'matched'
                : 'filtered'
              : ''
            return `<tr${rowClass ? ` class="${rowClass}"` : ''}><td>${this.escapeHtml(path)}</td><td>${this.escapeHtml(valueStr)}</td></tr>`
          })
          .join('\n')

        return `
          <div class="persisted-key-section">
            <div class="persisted-key-caption">
              <div class="caption-text">${this.escapeHtml(key)}</div>
              <div class="caption-buttons" data-key="${this.escapeHtml(key)}"></div>
            </div>
            <table>
              <thead>
                <tr>
                  <th class="thin">Path</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
          </div>
        `
      })
      .join('\n')

    this.setTextContent(
      '#persistedDataCount',
      hasFilters ? `${totalMatchedCount}/${totalCount}` : totalCount,
    )

    // Replace the entire ul content with the sections
    const ulElement = this.shadowRoot?.querySelector('#persistedDataContent ul')
    if (ulElement) {
      ulElement.innerHTML = `<li><div style="flex: 1; overflow-x: auto;">${sections}</div></li>`
      const liElement = ulElement.querySelector('li')
      if (liElement) {
        this.addPersistedDataButtons(liElement)
      }
    }
  }

  updatePersistedDataTreeDisplay() {
    const storage = this.persistedDataUseSessionStorage
      ? sessionStorage
      : localStorage
    const persistKeys = this.getDatastarPersistKeys()
    const data: Record<string, any> = {}

    for (const key of persistKeys) {
      const value = storage.getItem(key)
      if (value !== null) {
        try {
          data[key] = JSON.parse(value)
        } catch {
          data[key] = value
        }
      }
    }

    const hasFilters =
      this.persistedDataIncludeFilter || this.persistedDataExcludeFilter

    let totalMatchedCount = 0
    let totalCount = 0

    // Build HTML sections for each key with its own captioned tree view
    const sections = Object.entries(data)
      .map(([key, value]) => {
        const flatEntries = this.flattenObject(value)
        totalCount += flatEntries.length

        // If filters are active, check all nested paths to determine matches for this key
        const pathMatches: Set<string> = new Set()

        if (hasFilters) {
          for (const [path] of flatEntries) {
            if (
              this.matchesFilter(
                path,
                this.persistedDataIncludeFilter,
                this.persistedDataExcludeFilter,
                this.persistedDataUseExactMatch,
              )
            ) {
              totalMatchedCount++
              // Add this path and all its parent paths to the match set
              const parts = path.split('.')
              for (let i = 1; i <= parts.length; i++) {
                pathMatches.add(parts.slice(0, i).join('.'))
              }
            }
          }
        }

        // Build the display recursively with proper highlighting
        const buildTreeDisplay = (
          obj: any,
          prefix = '',
          indent = '  ',
        ): string[] => {
          const lines: string[] = []
          const entries = Object.entries(obj)

          entries.forEach(([objKey, objValue], index) => {
            const currentPath = prefix ? `${prefix}.${objKey}` : objKey
            const isLast = index === entries.length - 1
            const hasMatch = pathMatches.has(currentPath)

            if (
              objValue !== null &&
              typeof objValue === 'object' &&
              !Array.isArray(objValue)
            ) {
              // Handle nested objects
              if (hasFilters) {
                if (hasMatch) {
                  lines.push(`${indent}<span class="matched">"${objKey}": {`)
                } else {
                  lines.push(`${indent}<span class="filtered">"${objKey}": {`)
                }
              } else {
                lines.push(`${indent}"${objKey}": {`)
              }

              const nestedLines = buildTreeDisplay(
                objValue,
                currentPath,
                `${indent}  `,
              )
              lines.push(...nestedLines)

              if (hasFilters) {
                lines.push(`${indent}}${isLast ? '</span>' : ',</span>'}`)
              } else {
                lines.push(`${indent}}${isLast ? '' : ','}`)
              }
            } else {
              // Handle primitive values and arrays
              const valueStr = JSON.stringify(objValue, null, 2)
              const valueLines = valueStr.split('\n')

              if (hasFilters) {
                if (hasMatch) {
                  lines.push(
                    `${indent}<span class="matched">"${objKey}": ${this.escapeHtml(valueLines[0])}`,
                  )
                } else {
                  lines.push(
                    `${indent}<span class="filtered">"${objKey}": ${this.escapeHtml(valueLines[0])}`,
                  )
                }
                for (let i = 1; i < valueLines.length; i++) {
                  lines.push(`${indent}${valueLines[i]}`)
                }
                lines[lines.length - 1] += isLast ? '</span>' : ',</span>'
              } else {
                lines.push(
                  `${indent}"${objKey}": ${this.escapeHtml(valueLines[0])}`,
                )
                for (let i = 1; i < valueLines.length; i++) {
                  lines.push(`${indent}${this.escapeHtml(valueLines[i])}`)
                }
                if (!isLast) lines[lines.length - 1] += ','
              }
            }
          })

          return lines
        }

        // Build the tree content for this key
        const lines: string[] = []
        lines.push('{')
        lines.push(...buildTreeDisplay(value))
        lines.push('}')

        return `
          <div class="persisted-key-section">
            <div class="persisted-key-caption">
              <div class="caption-text">${this.escapeHtml(key)}</div>
              <div class="caption-buttons" data-key="${this.escapeHtml(key)}"></div>
            </div>
            <pre>${lines.join('\n')}</pre>
          </div>
        `
      })
      .join('\n')

    this.setTextContent(
      '#persistedDataCount',
      hasFilters ? `${totalMatchedCount}/${totalCount}` : totalCount,
    )

    // Replace the entire ul content with the sections
    const ulElement = this.shadowRoot?.querySelector('#persistedDataContent ul')
    if (ulElement) {
      ulElement.innerHTML = `<li><div style="flex: 1; overflow-x: auto;">${sections}</div></li>`
      const liElement = ulElement.querySelector('li')
      if (liElement) {
        this.addPersistedDataButtons(liElement)
      }
    }
  }

  setupPersistedDataHandlers() {
    if (!this.shadowRoot) return

    // Include filter
    const includeFilter = this.shadowRoot.querySelector(
      '#persistedDataIncludeFilter',
    ) as HTMLInputElement
    if (includeFilter) {
      // Set default values if empty
      if (!this.persistedDataIncludeFilter) {
        this.persistedDataIncludeFilter = '.*'
      }
      includeFilter.value = this.persistedDataIncludeFilter
      includeFilter.addEventListener('input', () => {
        this.persistedDataIncludeFilter = includeFilter.value
        this.saveState()
        this.updatePersistedDataDisplay()
        this.updatePersistedDataRegexDisplay()
      })
    }

    // Exclude filter
    const excludeFilter = this.shadowRoot.querySelector(
      '#persistedDataExcludeFilter',
    ) as HTMLInputElement
    if (excludeFilter) {
      // Set default values if empty
      if (!this.persistedDataExcludeFilter) {
        this.persistedDataExcludeFilter = '(^|\\.)_'
      }
      excludeFilter.value = this.persistedDataExcludeFilter
      excludeFilter.addEventListener('input', () => {
        this.persistedDataExcludeFilter = excludeFilter.value
        this.saveState()
        this.updatePersistedDataDisplay()
        this.updatePersistedDataRegexDisplay()
      })
    }

    // Exact match checkbox
    const exactMatch = this.shadowRoot.querySelector(
      '#persistedDataExactMatchCheckbox',
    ) as HTMLInputElement
    if (exactMatch) {
      exactMatch.checked = this.persistedDataUseExactMatch
      exactMatch.addEventListener('change', () => {
        this.persistedDataUseExactMatch = exactMatch.checked
        this.saveState()
        this.updatePersistedDataDisplay()
        this.updatePersistedDataRegexDisplay()
      })
    }

    // Table view checkbox
    const tableViewCheckbox = this.shadowRoot.querySelector(
      '#persistedDataTableViewCheckbox',
    ) as HTMLInputElement
    if (tableViewCheckbox) {
      tableViewCheckbox.checked = this.persistedDataTableView
      tableViewCheckbox.addEventListener('change', () => {
        this.persistedDataTableView = tableViewCheckbox.checked
        this.saveState()
        this.updatePersistedDataDisplay()
      })
    }

    // Storage toggle
    const storageToggle = this.shadowRoot.querySelector(
      '#persistedDataStorageToggle',
    ) as HTMLInputElement
    if (storageToggle) {
      storageToggle.checked = this.persistedDataUseSessionStorage
      storageToggle.addEventListener('change', () => {
        this.persistedDataUseSessionStorage = storageToggle.checked
        this.updateStorageTitle()
        this.saveState()
        this.updatePersistedDataCount()
        this.updatePersistedDataDisplay()
      })
    }

    // Refresh button
    const refreshBtn = this.shadowRoot.querySelector(
      '#refreshPersistedData',
    ) as HTMLButtonElement
    if (refreshBtn) {
      refreshBtn.addEventListener('click', () => {
        this.updatePersistedDataCount()
        this.updatePersistedDataDisplay()
      })
    }

    // Reset filters button
    const resetFilterBtn = this.shadowRoot.querySelector(
      '#resetPersistedDataFilters',
    ) as HTMLButtonElement
    if (resetFilterBtn) {
      resetFilterBtn.addEventListener('click', () => {
        this.persistedDataIncludeFilter = '.*'
        this.persistedDataExcludeFilter = '(^|\\.)_'
        this.persistedDataUseExactMatch = false

        includeFilter.value = this.persistedDataIncludeFilter
        excludeFilter.value = this.persistedDataExcludeFilter
        exactMatch.checked = this.persistedDataUseExactMatch

        this.saveState()
        this.updatePersistedDataDisplay()
        this.updatePersistedDataRegexDisplay()
      })
    }

    // Copy filter button
    const copyPersistedFilterBtn = this.shadowRoot.querySelector(
      '#copyPersistedDataFilterObject',
    ) as HTMLButtonElement
    if (copyPersistedFilterBtn) {
      copyPersistedFilterBtn.addEventListener('click', () => {
        this.copyPersistedDataFilterObject(copyPersistedFilterBtn)
      })
    }

    // Initial regex display update
    this.updatePersistedDataRegexDisplay()

    // Initial storage title update
    this.updateStorageTitle()
  }

  updateStorageTitle() {
    if (!this.shadowRoot) return

    const titleElement = this.shadowRoot.querySelector(
      '#persistedDataStorageTitle',
    ) as HTMLElement

    if (titleElement) {
      if (this.persistedDataUseSessionStorage) {
        titleElement.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" style="vertical-align: middle; margin-right: 4px;"><use href="#session-storage-icon"/></svg>Session Storage'
      } else {
        titleElement.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" style="vertical-align: middle; margin-right: 4px;"><use href="#local-storage-icon"/></svg>Local Storage'
      }
    }
  }

  updatePersistedDataRegexDisplay() {
    if (!this.shadowRoot) return

    const regexDisplay = this.shadowRoot.querySelector(
      '#persistedDataRegexDisplay',
    ) as HTMLElement
    const includeSpan = regexDisplay?.querySelector(
      '.regex-include',
    ) as HTMLElement
    const excludeSpan = regexDisplay?.querySelector(
      '.regex-exclude',
    ) as HTMLElement

    if (!regexDisplay || !includeSpan || !excludeSpan) return

    const include = this.persistedDataIncludeFilter || '.*'
    const exclude = this.persistedDataExcludeFilter || '(^|\\.)_'

    includeSpan.textContent =
      this.persistedDataUseExactMatch && this.persistedDataIncludeFilter
        ? this.exactMatchAsRegex(include).toString()
        : `/${include}/`
    excludeSpan.textContent =
      this.persistedDataUseExactMatch && this.persistedDataExcludeFilter
        ? this.exactMatchAsRegex(exclude).toString()
        : `/${exclude}/`
  }

  copyPersistedDataFilterObject(button: HTMLButtonElement) {
    const include = this.persistedDataIncludeFilter || '.*'
    const exclude = this.persistedDataExcludeFilter || '(^|\\.)_'

    const filterObj = {
      include: this.persistedDataUseExactMatch
        ? this.exactMatchAsRegex(include).toString()
        : `/${include}/`,
      exclude: this.persistedDataUseExactMatch
        ? this.exactMatchAsRegex(exclude).toString()
        : `/${exclude}/`,
    }

    const text = `{ include: ${filterObj.include}, exclude: ${filterObj.exclude} }`
    const spanButton = button.querySelector('span') as HTMLSpanElement
    navigator.clipboard
      .writeText(text)
      .then(() => {
        spanButton.textContent = 'Copied'
        spanButton.style.display = ''
      })
      .catch(() => {
        spanButton.textContent = 'Copy failed'
        spanButton.style.display = ''
      })
      .finally(() => {
        setTimeout(() => {
          spanButton.style.display = 'none'
        }, 2000)
      })
  }

  addPersistedDataButtons(liElement: Element) {
    const captionButtons = liElement.querySelectorAll('.caption-buttons')

    captionButtons.forEach((buttonContainer) => {
      const key = buttonContainer.getAttribute('data-key')
      if (!key || !this.persistedData[key]) return

      const value = this.persistedData[key]

      // Clone copy button from template
      const copyTemplate = this.shadowRoot?.querySelector(
        '#copy-button-template',
      ) as HTMLTemplateElement
      const copyButton = copyTemplate.content.cloneNode(
        true,
      ) as DocumentFragment
      const copyBtn = copyButton.querySelector('button') as HTMLButtonElement
      const copyBtnSpan = copyBtn.querySelector('span') as HTMLSpanElement

      copyBtn.onclick = () => {
        const jsonStr = JSON.stringify(value, null, 2)
        navigator.clipboard
          .writeText(jsonStr)
          .then(() => {
            copyBtnSpan.textContent = 'Copied'
            copyBtnSpan.style.display = ''
          })
          .catch(() => {
            copyBtnSpan.textContent = 'Copy Failed'
            copyBtnSpan.style.display = ''
          })
          .finally(() => {
            setTimeout(() => {
              copyBtnSpan.style.display = 'none'
            }, 2000)
          })
      }

      // Clone log button from template
      const logTemplate = this.shadowRoot?.querySelector(
        '#log-button-template',
      ) as HTMLTemplateElement
      const logButton = logTemplate.content.cloneNode(true) as DocumentFragment
      const logBtn = logButton.querySelector('button') as HTMLButtonElement
      const logBtnSpan = logBtn.querySelector('span') as HTMLSpanElement

      logBtn.onclick = () => {
        console.log(value)
        logBtnSpan.textContent = 'Logged'
        logBtnSpan.style.display = ''
        setTimeout(() => {
          logBtnSpan.style.display = 'none'
        }, 2000)
      }

      // Clone clear button from template
      const clearTemplate = this.shadowRoot?.querySelector(
        '#clear-button-template',
      ) as HTMLTemplateElement
      const clearButton = clearTemplate.content.cloneNode(
        true,
      ) as DocumentFragment
      const clearBtn = clearButton.querySelector('button') as HTMLButtonElement

      clearBtn.onclick = () => {
        if (confirm('Clear persisted data? This cannot be undone.')) {
          const storage = this.persistedDataUseSessionStorage
            ? sessionStorage
            : localStorage
          storage.removeItem(key)
          this.updatePersistedDataCount()
          this.updatePersistedDataDisplay()
        }
      }

      buttonContainer.appendChild(copyBtn)
      buttonContainer.appendChild(logButton)
      buttonContainer.appendChild(clearBtn)
    })

    // Add hover handlers for signal path highlighting
    this.addHoverHandlersToSignalPaths(liElement, 'persisted')
  }
}

customElements.define('datastar-inspector', DatastarInspector)
